import React, { useContext, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import moment from "moment-timezone";
import { toast } from "react-toastify";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Checkbox,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePlans from "../../hooks/usePlans";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import AddIcon from "@material-ui/icons/Add";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import PauseCircleOutlineIcon from "@material-ui/icons/PauseCircleOutline";
import PlayCircleOutlineIcon from "@material-ui/icons/PlayCircleOutline";
import VisibilityIcon from "@material-ui/icons/Visibility";
import CodeIcon from "@material-ui/icons/Code";
import ChatIcon from "@material-ui/icons/Chat";
import EventIcon from "@material-ui/icons/Event";
import PeopleIcon from "@material-ui/icons/People";
import RepeatIcon from "@material-ui/icons/Repeat";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import CampaignsBrowseBar from "../../components/CampaignsBrowseBar";
import ChartsSection from "../../components/CampaignShippingReports/ChartsSection";
import CampaignCard from "../../components/CampaignCard";
import StatusStoryPreview from "../../components/StatusStoryPreview";
import CampaignWizardLayout, { campaignWizardPaper } from "../../components/CampaignWizardLayout";
import DateTimePickerWithTimezone from "../../components/FormFields/DateTimePickerWithTimezone";
import AttachmentPreview from "../../components/AttachmentPreview";
import CampaignMessageComposer from "../../components/CampaignMessageComposer";
import { getCampaignTimezone, getCampaignTimezoneSync } from "../../helpers/getCampaignTimezone";

const useStyles = makeStyles(theme => ({
  paper: {
    flex: "0 0 auto",
    minHeight: 680,
    height: "auto",
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    overflow: "visible",
    borderRadius: 8,
    backgroundColor: theme.palette.background.paper,
    [theme.breakpoints.down("sm")]: {
      minHeight: 560,
      padding: theme.spacing(1.5)
    }
  },
  metricsRow: {
    flexShrink: 0,
    marginBottom: 16
  },
  listScroll: {
    flex: "0 0 auto",
    minHeight: 560,
    overflowX: "auto",
    overflowY: "visible",
    [theme.breakpoints.down("sm")]: {
      minHeight: 460
    }
  },
  gridWrap: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
    gap: 16,
    paddingBottom: 8
  },
  tableHeader: {
    backgroundColor: theme.mode === "dark" ? "#1f2937" : "#f8fafc",
    color: theme.mode === "dark" ? "#e5e7eb" : "#334155",
    fontWeight: 700
  },
  contactsBox: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    maxHeight: 220,
    overflow: "auto"
  },
  statusChipScheduled: {
    backgroundColor: "#E0F2FE",
    color: "#075985",
    fontWeight: 700
  },
  statusChipProcessing: {
    backgroundColor: "#FEF3C7",
    color: "#92400E",
    fontWeight: 700
  },
  statusChipCompleted: {
    backgroundColor: "#DCFCE7",
    color: "#166534",
    fontWeight: 700
  },
  statusChipFailed: {
    backgroundColor: "#FEE2E2",
    color: "#991B1B",
    fontWeight: 700
  },
  statusChipCancelled: {
    backgroundColor: "#E5E7EB",
    color: "#374151",
    fontWeight: 700
  },
  statusChipUpload: {
    backgroundColor: "#FCE7F3",
    color: "#9D174D",
    fontWeight: 700
  },
  table: {
    minWidth: 1100,
    [theme.breakpoints.down("xs")]: {
      minWidth: 1220,
    }
  },
  dialogContent: {
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(2),
    }
  },
  wizardContent: {
    padding: "0 !important",
    overflow: "hidden",
    flex: "1 1 auto",
    minHeight: 0,
    display: "flex",
  },
  dialogTitle: {
    fontWeight: 700,
    padding: theme.spacing(2, 3, 1),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1.5, 2),
    },
  },
  dialogActions: {
    [theme.breakpoints.down("xs")]: {
      padding: "8px 16px calc(8px + env(safe-area-inset-bottom, 0px))",
      flexWrap: "wrap",
      justifyContent: "stretch",
      gap: theme.spacing(1),
      "& > *": {
        flex: "1 1 auto",
        minHeight: 44,
        margin: 0,
      },
    }
  },
  metricCard: {
    padding: theme.spacing(2),
    borderRadius: 8,
    height: "100%"
  },
  viewList: {
    marginTop: 4,
    display: "flex",
    flexDirection: "column",
    gap: 8
  },
  viewRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    alignItems: "center",
    padding: "8px 0",
    borderBottom: `1px solid ${theme.palette.divider}`,
    "&:last-child": {
      borderBottom: "none"
    }
  },
  shippingCard: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1)
  },
  codeBox: {
    padding: theme.spacing(2),
    borderRadius: 8,
    backgroundColor: theme.palette.type === "dark" ? "#111827" : "#1f2937",
    color: "#f9fafb",
    fontFamily: "monospace",
    fontSize: 12,
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere"
  }
}));

const initialForm = {
  name: "",
  whatsappId: "",
  scheduledDate: "",
  targetType: "all",
  contactIds: [],
  message1: "",
  message2: "",
  message3: "",
  message4: "",
  message5: "",
  messageInterval: 20,
  isRecurring: false,
  recurrenceType: "diaria",
  recurrenceInterval: 1,
  recurrenceDaysOfWeek: "[]",
  recurrenceDayOfMonth: "",
  recurrenceEndDate: "",
  maxExecutions: ""
};

const normalizeDateString = value => value ? String(value).replace("T", " ").substring(0, 19) : "";

const getSuggestedScheduledDate = campaignTimezone => {
  const suggested = moment.tz(campaignTimezone || getCampaignTimezoneSync()).add(5, "minutes");
  const remainder = suggested.minute() % 5;

  if (remainder > 0) {
    suggested.add(5 - remainder, "minutes");
  }

  return suggested.second(0).millisecond(0).format("YYYY-MM-DD HH:mm:ss");
};

const formatDate = value => {
  const normalized = normalizeDateString(value);
  return normalized || "-";
};

const statusLabel = {
  scheduled: "Programada",
  processing: "Procesando",
  completed: "Completada",
  failed: "Fallida",
  cancelled: "Cancelada",
  draft_upload: "Pendiente multimedia"
};

const StatusCampaigns = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const { getPlanCompany } = usePlans();
  const [statusPerDay, setStatusPerDay] = useState(null);
  const isMobile = useMediaQuery(theme => theme.breakpoints.down("sm"));
  const statusChipClass = status => ({
    scheduled: classes.statusChipScheduled,
    processing: classes.statusChipProcessing,
    completed: classes.statusChipCompleted,
    failed: classes.statusChipFailed,
    cancelled: classes.statusChipCancelled,
    draft_upload: classes.statusChipUpload,
    sent: classes.statusChipCompleted,
    pending: classes.statusChipScheduled
  }[status] || classes.statusChipCancelled);
  const [campaigns, setCampaigns] = useState([]);
  const [whatsapps, setWhatsapps] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [media, setMedia] = useState(null);
  const [loading, setLoading] = useState(false);
  const [timezone, setTimezone] = useState(getCampaignTimezoneSync());
  const [shippingOpen, setShippingOpen] = useState(false);
  const [shippingCampaign, setShippingCampaign] = useState(null);
  const [shippings, setShippings] = useState([]);
  const [shippingLoading, setShippingLoading] = useState(false);
  const [shippingSummary, setShippingSummary] = useState({ sent: 0, viewed: 0, failed: 0 });
  const [shippingView, setShippingView] = useState("seen");
  const [metrics, setMetrics] = useState(null);
  const [searchParam, setSearchParam] = useState("");
  const [viewMode, setViewMode] = useState("grid");
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(12);
  const [formSection, setFormSection] = useState("datos");

  useEffect(() => {
    if (!user?.companyId) return;
    let active = true;
    getPlanCompany(undefined, user.companyId)
      .then(planConfigs => {
        if (!active) return;
        const max = Number(planConfigs?.plan?.maxStatusCampaignsPerDay);
        setStatusPerDay(Number.isFinite(max) ? max : null);
      })
      .catch(() => {
        if (active) setStatusPerDay(null);
      });
    return () => {
      active = false;
    };
  }, [user?.companyId]);

  const selectedContacts = useMemo(
    () => contacts.filter(contact => form.contactIds.includes(contact.id)),
    [contacts, form.contactIds]
  );
  const fetchCampaigns = async () => {
    const { data } = await api.get("/status-campaigns");
    setCampaigns(Array.isArray(data) ? data : []);
  };

  const fetchMetrics = async () => {
    const { data } = await api.get("/status-campaigns/metrics");
    setMetrics(data);
  };

  const fetchResources = async () => {
    const [{ data: whatsappData }, { data: contactsData }, resolvedTimezone] = await Promise.all([
      api.get("/whatsapp/?session=0"),
      api.get("/status-campaigns/audience"),
      getCampaignTimezone()
    ]);
    setWhatsapps(Array.isArray(whatsappData) ? whatsappData : []);
    setContacts(Array.isArray(contactsData) ? contactsData : []);
    setTimezone(resolvedTimezone);
  };

  useEffect(() => {
    fetchCampaigns().catch(toastError);
    fetchMetrics().catch(() => {});
    fetchResources().catch(toastError);
    const timer = setInterval(() => {
      fetchCampaigns().catch(() => {});
      fetchMetrics().catch(() => {});
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!shippingOpen || !shippingCampaign?.id) return undefined;
    const timer = setInterval(() => {
      loadShipping(shippingCampaign, shippingView, true);
    }, 20000);
    return () => clearInterval(timer);
  }, [shippingOpen, shippingCampaign, shippingView]);

  const handleOpen = campaign => {
    if (campaign) {
      let contactIds = [];
      try {
        contactIds = campaign.originalContactIds ? JSON.parse(campaign.originalContactIds) : [];
      } catch {
        contactIds = [];
      }

      setEditing(campaign);
      setForm({
        ...initialForm,
        ...campaign,
        whatsappId: campaign.whatsappId || "",
        scheduledDate: normalizeDateString(campaign.scheduledDate),
        recurrenceEndDate: normalizeDateString(campaign.recurrenceEndDate),
        contactIds,
        isRecurring: Boolean(campaign.isRecurring)
      });
    } else {
      setEditing(null);
      setForm({
        ...initialForm,
        scheduledDate: getSuggestedScheduledDate(timezone)
      });
    }
    setMedia(null);
    setFormSection("datos");
    setOpen(true);
  };

  const setField = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const toggleContact = contactId => {
    setForm(prev => ({
      ...prev,
      contactIds: prev.contactIds.includes(contactId)
        ? prev.contactIds.filter(id => id !== contactId)
        : [...prev.contactIds, contactId]
    }));
  };

  const selectedFormWhatsapp = useMemo(
    () => whatsapps.find(whatsapp => whatsapp.id === Number(form.whatsappId)),
    [whatsapps, form.whatsappId]
  );
  const isTelegramStatus = String(selectedFormWhatsapp?.type || "").toLowerCase() === "telegram";

  const save = async () => {
    if (!form.whatsappId) {
      toast.error("Selecciona una conexión");
      return;
    }
    if (isTelegramStatus && !media && !editing?.mediaPath) {
      toast.error("Telegram exige foto o video para publicar una historia. El texto solo no está permitido.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        hasMediaUpload: Boolean(media),
        scheduledDate: normalizeDateString(form.scheduledDate),
        recurrenceEndDate: form.recurrenceEndDate ? normalizeDateString(form.recurrenceEndDate) : null
      };

      const { data } = editing
        ? await api.put(`/status-campaigns/${editing.id}`, payload)
        : await api.post("/status-campaigns", payload);

      if (media) {
        const formData = new FormData();
        formData.append("typeArch", "status-campaigns");
        formData.append("fileId", String(data.id));
        formData.append("file", media);
        await api.post(`/status-campaigns/${data.id}/media-upload`, formData);
      }

      toast.success(isTelegramStatus ? "Historia de Telegram programada" : "Campaña de estados guardada", {
        autoClose: 4000,
      });
      setOpen(false);
      await fetchCampaigns();
      await fetchMetrics();
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const remove = async id => {
    if (!window.confirm("Eliminar campaña de estados?")) return;
    try {
      await api.delete(`/status-campaigns/${id}`);
      await fetchCampaigns();
      await fetchMetrics();
    } catch (err) {
      toastError(err);
    }
  };

  const setCampaignStatus = async (campaign, action) => {
    try {
      await api.post(`/status-campaigns/${campaign.id}/${action}`);
      await fetchCampaigns();
      await fetchMetrics();
    } catch (err) {
      toastError(err);
    }
  };

  const loadShipping = async (campaign, view = shippingView, silent = false) => {
    if (!campaign?.id) return;
    if (!silent) setShippingLoading(true);
    try {
      const { data } = await api.get(`/status-campaigns/${campaign.id}/shipping`, { params: { view } });
      const rows = Array.isArray(data) ? data : data?.rows;
      setShippings(Array.isArray(rows) ? rows : []);
      setShippingSummary(data?.summary || { sent: 0, viewed: 0, failed: 0 });
    } catch (err) {
      if (!silent) toastError(err);
    } finally {
      if (!silent) setShippingLoading(false);
    }
  };

  const openShipping = async campaign => {
    setShippingCampaign(campaign);
    setShippingView("seen");
    setShippings([]);
    setShippingSummary({ sent: 0, viewed: 0, failed: 0 });
    setShippingOpen(true);
    await loadShipping(campaign, "seen");
  };

  const getNextExecution = campaign => {
    if (campaign.status === "completed") return "Finalizada";
    if (campaign.status === "cancelled") return "Cancelada";
    return formatDate(campaign.isRecurring ? campaign.nextScheduledAt || campaign.scheduledDate : campaign.scheduledDate);
  };

  const filteredCampaigns = campaigns.filter(campaign => {
    if (!campaign) return false;
    if (searchParam && searchParam.trim()) {
      const term = searchParam.toLowerCase();
      const name = String(campaign.name || "").toLowerCase();
      const connection = String(campaign.whatsapp?.name || "").toLowerCase();
      if (!name.includes(term) && !connection.includes(term)) return false;
    }
    if (typeFilter === "unica" && campaign.isRecurring) return false;
    if (typeFilter === "recurrente" && !campaign.isRecurring) return false;
    if (statusFilter && campaign.status !== statusFilter) return false;
    return true;
  });
  const pagedCampaigns = filteredCampaigns.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  useEffect(() => {
    setPage(0);
  }, [searchParam, typeFilter, statusFilter]);

  useEffect(() => {
    const lastPage = Math.max(0, Math.ceil(filteredCampaigns.length / rowsPerPage) - 1);
    if (page > lastPage) setPage(lastPage);
  }, [filteredCampaigns.length, rowsPerPage, page]);

  return (
    <MainContainer flow>
      <MainHeader>
        <Title subtitle="Publica para quienes conversan con la línea y revisa quién abrió el estado.">
          Estados / Historias
        </Title>
        <MainHeaderButtonsWrapper>
          <Button color="default" variant="outlined" startIcon={<CodeIcon />} onClick={() => history.push("/messages-api#estados")}>
            API
          </Button>
          <Button color="primary" variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen(null)} style={{ borderRadius: 999, textTransform: "none", fontWeight: 700 }}>
            Nueva campaña
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Grid container spacing={2} className={classes.metricsRow}>
        <Grid item xs={6} md={3}>
          <Paper className={classes.metricCard}>
            <Typography variant="caption" color="textSecondary">Publicaciones · {metrics?.month?.label || "este mes"}</Typography>
            <Typography variant="h5">{metrics?.month?.publications || 0}</Typography>
            <Typography variant="body2">Programadas: {metrics?.campaigns?.scheduled || 0}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} md={3}>
          <Paper className={classes.metricCard}>
            <Typography variant="caption" color="textSecondary">Envíos del mes</Typography>
            <Typography variant="h5">{metrics?.month?.sent || 0}</Typography>
            <Typography variant="body2">Errores: {metrics?.shippings?.failed || 0}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} md={3}>
          <Paper className={classes.metricCard}>
            <Typography variant="caption" color="textSecondary">Lo vieron</Typography>
            <Typography variant="h5">{metrics?.month?.viewed || 0}</Typography>
            <Typography variant="body2">{metrics?.month?.viewers || 0} personas</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} md={3}>
          <Paper className={classes.metricCard}>
            <Typography variant="caption" color="textSecondary">Última publicación</Typography>
            <Typography variant="body1" style={{ fontWeight: 700 }}>{metrics?.lastCampaign?.name || "—"}</Typography>
            <Typography variant="body2">{formatDate(metrics?.lastCampaign?.lastExecutedAt)}</Typography>
          </Paper>
        </Grid>
        {(Number(metrics?.month?.sent || 0) + Number(metrics?.month?.viewed || 0) + Number(metrics?.shippings?.failed || 0)) > 0 && (
          <Grid item xs={12} md={5}>
            <ChartsSection
              reportType="status"
              metrics={{
                totalDelivered: metrics?.month?.viewed || 0,
                totalPending: Math.max(0, Number(metrics?.month?.sent || 0) - Number(metrics?.month?.viewed || 0)),
                totalFailed: metrics?.shippings?.failed || 0
              }}
            />
          </Grid>
        )}
        <Grid item xs={12} md={(Number(metrics?.month?.sent || 0) + Number(metrics?.month?.viewed || 0) + Number(metrics?.shippings?.failed || 0)) > 0 ? 7 : 12}>
          <Paper className={classes.metricCard} style={{ height: "100%", boxSizing: "border-box" }}>
            <Typography variant="caption" color="textSecondary">Personas que abrieron un estado este mes</Typography>
            {(metrics?.recentViews || []).length === 0 ? (
              <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                Cuando alguien abra un estado, su nombre aparece aquí. Enviado no significa que lo haya visto.
              </Typography>
            ) : (
              <Box className={classes.viewList}>
                {metrics.recentViews.map(view => (
                  <Box key={view.id} className={classes.viewRow}>
                    <Box minWidth={0}>
                      <Typography variant="body2" style={{ fontWeight: 700 }} noWrap>{view.contactName || view.contactNumber}</Typography>
                      <Typography variant="caption" color="textSecondary" noWrap>{view.campaignName}</Typography>
                    </Box>
                    <Typography variant="caption" color="textSecondary">{formatDate(view.viewedAt)}</Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Paper className={classes.paper}>
        <Box style={{ flexShrink: 0 }}>
        <CampaignsBrowseBar
          searchValue={searchParam}
          onSearch={event => setSearchParam(event.target.value)}
          searchPlaceholder="Buscar"
          typeValue={typeFilter}
          onTypeChange={setTypeFilter}
          typeOptions={[
            { value: "", label: "Tipo" },
            { value: "unica", label: "Única" },
            { value: "recurrente", label: "Recurrente" },
          ]}
          statusValue={statusFilter}
          onStatusChange={setStatusFilter}
          statusOptions={[
            { value: "", label: "Estado" },
            { value: "scheduled", label: "Programada" },
            { value: "processing", label: "Procesando" },
            { value: "completed", label: "Completada" },
            { value: "failed", label: "Fallida" },
            { value: "cancelled", label: "Cancelada" },
            { value: "draft_upload", label: "Pendiente multimedia" },
          ]}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
        />
        </Box>
        {viewMode === "grid" ? (
          <Box className={classes.listScroll}>
            <Box className={classes.gridWrap}>
              {filteredCampaigns.length === 0 ? (
                <Typography variant="body2" color="textSecondary">No hay campañas de estados creadas.</Typography>
              ) : (
                pagedCampaigns.map(campaign => (
                  <CampaignCard
                    key={campaign.id}
                    name={campaign.name}
                    dateLabel={getNextExecution(campaign)}
                    connectionName={campaign.whatsapp?.name || campaign.whatsappId}
                    countLabel={campaign.targetType === "all" ? `${campaign.totalContacts || campaign.sentContacts || 0} de la línea` : `${campaign.sentContacts || 0} con acceso`}
                    statusChip={
                      <Chip
                        size="small"
                        className={statusChipClass(campaign.status)}
                        label={statusLabel[campaign.status] || campaign.status}
                      />
                    }
                    onOpen={() => openShipping(campaign)}
                    actions={
                      <>
                        {campaign.status === "cancelled" ? (
                          <IconButton size="small" onClick={() => setCampaignStatus(campaign, "restart")}>
                            <PlayCircleOutlineIcon />
                          </IconButton>
                        ) : (
                          <IconButton size="small" onClick={() => setCampaignStatus(campaign, "cancel")}>
                            <PauseCircleOutlineIcon />
                          </IconButton>
                        )}
                        <Button size="small" onClick={() => handleOpen(campaign)}>Editar</Button>
                        <IconButton size="small" onClick={() => remove(campaign.id)}>
                          <DeleteOutlineIcon />
                        </IconButton>
                      </>
                    }
                  />
                ))
              )}
            </Box>
          </Box>
        ) : (
        <Box className={classes.listScroll}>
        <Table size="small" className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell className={classes.tableHeader}>Nombre</TableCell>
              <TableCell className={classes.tableHeader}>Adjunto</TableCell>
              <TableCell className={classes.tableHeader}>Conexión</TableCell>
              <TableCell className={classes.tableHeader}>Próxima ejecución</TableCell>
              <TableCell className={classes.tableHeader}>Última ejecución</TableCell>
              <TableCell className={classes.tableHeader}>Quién puede verlo</TableCell>
              <TableCell className={classes.tableHeader}>Alcance</TableCell>
              <TableCell className={classes.tableHeader}>Estado</TableCell>
              <TableCell className={classes.tableHeader} align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
              {pagedCampaigns.map(campaign => (
              <TableRow
                key={campaign.id}
                hover
                style={{ cursor: "pointer" }}
                onClick={() => openShipping(campaign)}
              >
                <TableCell>{campaign.name}</TableCell>
                <TableCell>
                  {campaign.mediaPath || campaign.mediaName ? (
                    <AttachmentPreview
                      mediaPath={campaign.mediaPath}
                      mediaName={campaign.mediaName}
                      size="compact"
                    />
                  ) : (
                    "—"
                  )}
                </TableCell>
                <TableCell>{campaign.whatsapp?.name || campaign.whatsappId}</TableCell>
                <TableCell>
                  <Typography variant="body2">{getNextExecution(campaign)}</Typography>
                  <Typography variant="caption" color="textSecondary">{timezone}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{formatDate(campaign.lastExecutedAt)}</Typography>
                  <Typography variant="caption" color="textSecondary">
                    Ejecuciones: {campaign.executionCount || 0}
                  </Typography>
                </TableCell>
                <TableCell>{campaign.targetType === "all" ? "Contactos de la línea, lotes de 50" : `${campaign.totalContacts || 0} contactos seleccionados`}</TableCell>
                <TableCell>
                  <Typography variant="body2">{campaign.sentContacts || 0} contactos con acceso</Typography>
                  <Typography variant="caption" color={campaign.failedContacts > 0 ? "error" : "textSecondary"}>
                    {campaign.failedContacts || 0} errores de publicación
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    className={statusChipClass(campaign.status)}
                    label={statusLabel[campaign.status] || campaign.status}
                  />
                </TableCell>
                <TableCell align="right" onClick={e => e.stopPropagation()}>
                  <Tooltip title="Ver alcance del estado">
                    <IconButton size="small" onClick={() => openShipping(campaign)}>
                      <VisibilityIcon />
                    </IconButton>
                  </Tooltip>
                  {campaign.status === "cancelled" ? (
                    <IconButton size="small" onClick={() => setCampaignStatus(campaign, "restart")}>
                      <PlayCircleOutlineIcon />
                    </IconButton>
                  ) : (
                    <IconButton size="small" onClick={() => setCampaignStatus(campaign, "cancel")}>
                      <PauseCircleOutlineIcon />
                    </IconButton>
                  )}
                  <Button size="small" onClick={() => handleOpen(campaign)}>Editar</Button>
                  <IconButton size="small" onClick={() => remove(campaign.id)}>
                    <DeleteOutlineIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {filteredCampaigns.length === 0 && (
              <TableRow>
                <TableCell colSpan={9}>No hay campañas de estados creadas.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        </Box>
        )}
        {filteredCampaigns.length > 0 && (
          <TablePagination
            component="div"
            count={filteredCampaigns.length}
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[12, 24, 48]}
            labelRowsPerPage="Registros por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          />
        )}
      </Paper>

      <Dialog open={shippingOpen} onClose={() => setShippingOpen(false)} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle>{shippingCampaign?.name || "Envío"}</DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <Box mb={2}>
            <Typography variant="body2">
              {shippingSummary.viewed} lo vieron · {shippingSummary.sent} enviados · {shippingSummary.failed} errores
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Enviado significa que quedó publicado para ese contacto. Visto significa que lo abrió. Quien no tiene el número guardado no lo ve.
            </Typography>
          </Box>
          <Box display="flex" mb={2} style={{ gap: 8 }}>
            <Button
              size="small"
              variant={shippingView === "seen" ? "contained" : "outlined"}
              color="primary"
              onClick={() => {
                setShippingView("seen");
                loadShipping(shippingCampaign, "seen");
              }}
            >
              Lo vieron
            </Button>
            <Button
              size="small"
              variant={shippingView === "all" ? "contained" : "outlined"}
              onClick={() => {
                setShippingView("all");
                loadShipping(shippingCampaign, "all");
              }}
            >
              Todos los envíos
            </Button>
          </Box>
          {shippingLoading && <Typography variant="body2">Cargando…</Typography>}
          {!shippingLoading && shippingView === "all" && shippings.length >= 400 && (
            <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
              Se muestran los 400 envíos más recientes. Los que lo abrieron están en «Lo vieron».
            </Typography>
          )}
          {!shippingLoading && shippings.length === 0 && (
            <Typography variant="body2" color="textSecondary">
              {shippingView === "seen" ? "Nadie lo ha abierto todavía." : "Aún no hay envíos en esta campaña."}
            </Typography>
          )}
          {isMobile ? (
            shippings.map(row => (
              <Box key={row.id} className={classes.shippingCard}>
                <Typography variant="body2" style={{ fontWeight: 700 }}>{row.contact?.name || "Sin nombre"}</Typography>
                <Typography variant="caption" color="textSecondary">{row.contact?.number || row.contactJid}</Typography>
                <Box display="flex" alignItems="center" mt={1} style={{ gap: 8, flexWrap: "wrap" }}>
                  <Chip size="small" className={statusChipClass(row.status)} label={row.status === "sent" ? "Enviado" : row.status === "failed" ? "Fallido" : row.status} />
                  <Chip size="small" className={row.viewedAt ? classes.statusChipCompleted : classes.statusChipCancelled} label={row.viewedAt ? "Visto" : "Sin ver"} />
                </Box>
                <Typography variant="caption" color="textSecondary" display="block">
                  {row.viewedAt ? `Abierto ${formatDate(row.viewedAt)}` : `Enviado ${formatDate(row.sentAt || row.createdAt)}`}
                </Typography>
              </Box>
            ))
          ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell className={classes.tableHeader}>Contacto</TableCell>
                <TableCell className={classes.tableHeader}>Número</TableCell>
                <TableCell className={classes.tableHeader}>Envío</TableCell>
                <TableCell className={classes.tableHeader}>Visto</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {shippings.map(row => (
                <TableRow key={row.id}>
                  <TableCell>{row.contact?.name || "—"}</TableCell>
                  <TableCell>{row.contact?.number || row.contactJid}</TableCell>
                  <TableCell>
                    <Chip size="small" className={statusChipClass(row.status)} label={row.status === "sent" ? "Enviado" : row.status === "failed" ? "Fallido" : row.status} />
                    <Typography variant="caption" color="textSecondary" display="block">{formatDate(row.sentAt || row.createdAt)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" className={row.viewedAt ? classes.statusChipCompleted : classes.statusChipCancelled} label={row.viewedAt ? "Visto" : "Sin ver"} />
                    <Typography variant="caption" color="textSecondary" display="block">{row.viewedAt ? formatDate(row.viewedAt) : "Aún no lo abre"}</Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          )}
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button onClick={() => setShippingOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ style: campaignWizardPaper(isMobile) }}
      >
        <DialogTitle className={classes.dialogTitle}>{editing ? "Editar publicación programada" : "Nueva campaña de estados / historias"}</DialogTitle>
        <DialogContent className={classes.wizardContent}>
          <CampaignWizardLayout
            sections={[
              { id: "datos", label: "Datos", icon: <EventIcon fontSize="small" /> },
              { id: "mensaje", label: "Mensaje", icon: <ChatIcon fontSize="small" /> },
              { id: "audiencia", label: "Audiencia", icon: <PeopleIcon fontSize="small" /> },
              { id: "programa", label: "Programación", icon: <RepeatIcon fontSize="small" /> },
            ]}
            activeSection={formSection}
            onSectionChange={setFormSection}
            preview={
              <StatusStoryPreview
                connectionName={whatsapps.find(w => String(w.id) === String(form.whatsappId))?.name}
                channel={isTelegramStatus ? "telegram" : "whatsapp"}
                messages={[1, 2, 3, 4, 5].map(number => form[`message${number}`])}
                attachment={media}
                mediaPath={!media ? editing?.mediaPath : ""}
                mediaName={media?.name || editing?.mediaName}
              />
            }
          >
            <Grid container spacing={2}>
              {formSection === "datos" && (
                <>
                  <Grid item xs={12}>
                    <TextField fullWidth label="Nombre" variant="outlined" value={form.name} onChange={e => setField("name", e.target.value)} />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel>Conexión</InputLabel>
                      <Select label="Conexión" value={form.whatsappId} onChange={e => setField("whatsappId", e.target.value)}>
                        {whatsapps.map(whatsapp => (
                          <MenuItem key={whatsapp.id} value={whatsapp.id}>
                            {whatsapp.name}{" "}
                            {String(whatsapp.type || "").toLowerCase() === "telegram" ? "(Telegram)" : "(WhatsApp)"}{" "}
                            {whatsapp.status ? `(${whatsapp.status})` : ""}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <DateTimePickerWithTimezone
                      label="Fecha"
                      timezone={timezone}
                      value={form.scheduledDate}
                      onChange={value => setField("scheduledDate", value || "")}
                    />
                  </Grid>
                </>
              )}

              {formSection === "mensaje" && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="textSecondary">
                      Negrita, cursiva, tachado, emoji y variables, igual que en las campañas. Saludo y hora se completan al publicar. Nombre, número y columnas quedan escritos en el texto, porque todos ven el mismo estado.
                    </Typography>
                  </Grid>
                  {[1, 2, 3, 4, 5].map(number => (
                    <Grid item xs={12} key={number}>
                      <CampaignMessageComposer
                        label={`Texto ${number}${number === 1 ? "" : " (opcional)"}`}
                        placeholder="Escribe el estado. Puedes usar *negrita*, _cursiva_ y variables."
                        value={form[`message${number}`] || ""}
                        onChange={value => setField(`message${number}`, value)}
                        rows={3}
                        maxLength={1000}
                      />
                    </Grid>
                  ))}
                  <Grid item xs={12}>
                    <Button variant="outlined" component="label" style={{ textTransform: "none" }}>
                      {isTelegramStatus ? "Foto o video (obligatorio en Telegram)" : "Multimedia"}
                      <input
                        hidden
                        type="file"
                        accept={isTelegramStatus ? "image/*,video/*" : "image/*,video/*,audio/*"}
                        onChange={e => setMedia(e.target.files?.[0] || null)}
                      />
                    </Button>
                    {isTelegramStatus && (
                      <Typography variant="caption" display="block" color="textSecondary" style={{ marginTop: 8 }}>
                        Telegram Stories: solo foto/video, visibles para contactos de la cuenta. Texto solo no está permitido.
                      </Typography>
                    )}
                    <Box mt={1}>
                      {(media || editing?.mediaPath) ? (
                        <AttachmentPreview
                          file={media}
                          mediaPath={!media ? editing?.mediaPath : ""}
                          mediaName={media?.name || editing?.mediaName}
                          size="default"
                          onDelete={media ? () => setMedia(null) : undefined}
                        />
                      ) : (
                        <Typography variant="body2">Sin archivo seleccionado</Typography>
                      )}
                    </Box>
                  </Grid>
                </>
              )}

              {formSection === "audiencia" && (
                <>
                  <Grid item xs={12}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel>{isTelegramStatus ? "Quién puede ver la historia" : "Quién puede ver el estado"}</InputLabel>
                      <Select
                        label={isTelegramStatus ? "Quién puede ver la historia" : "Quién puede ver el estado"}
                        value={form.targetType}
                        onChange={e => setField("targetType", e.target.value)}
                      >
                        <MenuItem value="all">Contactos de la línea</MenuItem>
                        <MenuItem value="contacts">Contactos seleccionados</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="textSecondary">
                      {isTelegramStatus
                        ? "Telegram publica una historia en la cuenta. No abre chats ni envía mensajes directos. La audiencia depende de la privacidad de Stories de la cuenta."
                        : `Una publicación llega a los contactos que conversan con la línea. En el teléfono se ve un estado por cada 50 contactos: 100 contactos son dos estados seguidos. WhatsApp lo muestra a quien tiene el número guardado. Quien nunca escribió no entra.${statusPerDay > 0 ? ` Tu plan incluye ${statusPerDay} ${statusPerDay === 1 ? "publicación" : "publicaciones"} al día.` : ""}`}
                    </Typography>
                  </Grid>
                  {form.targetType === "contacts" && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2">Seleccionados: {selectedContacts.length}</Typography>
                      <List dense className={classes.contactsBox}>
                        {contacts.map(contact => (
                          <ListItem key={contact.id} button onClick={() => toggleContact(contact.id)}>
                            <ListItemIcon>
                              <Checkbox edge="start" checked={form.contactIds.includes(contact.id)} tabIndex={-1} disableRipple />
                            </ListItemIcon>
                            <ListItemText primary={contact.name} secondary={contact.number} />
                          </ListItem>
                        ))}
                      </List>
                    </Grid>
                  )}
                </>
              )}

              {formSection === "programa" && (
                <>
                  <Grid item xs={12}>
                    <TextField fullWidth variant="outlined" label="Intervalo entre estados (segundos)" type="number" value={form.messageInterval} onChange={e => setField("messageInterval", e.target.value)} />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={<Switch checked={form.isRecurring} onChange={e => setField("isRecurring", e.target.checked)} />}
                      label="Publicar de forma recurrente"
                    />
                  </Grid>
                  {form.isRecurring && (
                    <>
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth variant="outlined">
                          <InputLabel>Recurrencia</InputLabel>
                          <Select label="Recurrencia" value={form.recurrenceType} onChange={e => setField("recurrenceType", e.target.value)}>
                            <MenuItem value="horaria">Horaria</MenuItem>
                            <MenuItem value="diaria">Diaria</MenuItem>
                            <MenuItem value="semanal">Semanal</MenuItem>
                            <MenuItem value="quincenal">Quincenal</MenuItem>
                            <MenuItem value="mensual">Mensual</MenuItem>
                            <MenuItem value="anual">Anual</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField fullWidth variant="outlined" type="number" label="Cada" value={form.recurrenceInterval} onChange={e => setField("recurrenceInterval", e.target.value)} />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField fullWidth variant="outlined" type="number" label="Máximo de ejecuciones" value={form.maxExecutions} onChange={e => setField("maxExecutions", e.target.value)} />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <DateTimePickerWithTimezone
                          label="Finaliza en"
                          timezone={timezone}
                          value={form.recurrenceEndDate}
                          onChange={value => setField("recurrenceEndDate", value || "")}
                        />
                      </Grid>
                    </>
                  )}
                </>
              )}
            </Grid>
          </CampaignWizardLayout>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button onClick={() => setOpen(false)} style={{ textTransform: "none" }}>Cancelar</Button>
          <Button color="primary" variant="contained" disabled={loading} onClick={save} style={{ borderRadius: 999, textTransform: "none", fontWeight: 700 }}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

    </MainContainer>
  );
};

export default StatusCampaigns;
